/* eslint-disable max-statements */
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient, QueryClient } from 'react-query';
import moment from 'moment';
import Pagination from '../../Tables/pagination';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSelectedSection } from '../../../redux/actions/sideBarAction';
import TableLoadingSkeleton from '../../Utils/LoadingAnimations/TableLoadingSkeleton';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import LazyImage from '../../Tables/TablesList/Components/imageComponent';
import { globalEnv } from '../../../config/baseConfig';
import { setSearchPlaceholderAction } from '../../../redux/actions/searchBarAction';
import {
	HeaderItem,
	sortingOrder
} from '../../Tables/TablesList/Components/common';
import { DataNotFoundAnimation } from '../../Utils/ErrorAnimations/dataNotFoundAnim';
import { axiosInstance } from '../../../config/axiosInstance';

export default function DeviceIdUserList() {
	const queryClient = useQueryClient();
	const [page, setPage] = useState(1);
	const [sortOrder, setSortOrder] = useState(sortingOrder.DESCENDING);
	const [selectedHeader, setSelectedHeader] = useState('createdAt');
	const [usersCount, setUsersCount] = useState(0);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);
	const { searchQuery }: { searchQuery: string } = useTypedSelector(
		(state) => state.searchbar
	);

	const [dimensions, setDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight
	});
	useEffect(() => {
		function handleResize() {
			setDimensions({
				width: window.innerWidth,
				height: window.innerHeight
			});
		}
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const dispatch = useDispatch();
	const { deviceId } = useParams();

	const { data, isError, isFetching, isLoading } = useQuery(
		['users', page, selectedHeader, sortOrder, searchQuery],
		() => fetchUsers(page, ACCESS_TOKEN),
		{
			cacheTime: 0,
			keepPreviousData: false,
			staleTime: 5000,
			refetchOnWindowFocus: false
		}
	);

	useEffect(() => {
		dispatch(setSelectedSection('User Documents'));
		dispatch(setSearchPlaceholderAction('Search for Users'));
	}, []);

	// Prefetch the next page!
	useEffect(() => {
		if (data?.hasMore) {
			queryClient.prefetchQuery(['users', page + 1], () =>
				fetchUsers(page + 1, ACCESS_TOKEN)
			);
		}
	}, [data, page, queryClient]);

	useEffect(() => {
		if (data != null) {
			setUsersCount(data.meta.total);
		}
	}, [data]);

	useEffect(() => {
		const intervalId = setInterval(() => {
			window.location.reload();
		}, 1000 * 60 * 20);
		return () => clearInterval(intervalId);
	}, []);

	async function fetchUsers(page: number, token: string) {
		const config = {
			headers: {
				Authorization: 'Bearer ' + token
			}
		};
		const { data } = await axiosInstance.get(
			globalEnv.BASE_URL +
				`/admin/user/find-users-by-deviceId/${deviceId}?page=${page}`,
			config
		);
		return data;
	}

	const TableHeaders = () => {
		return (
			<tr>
				<HeaderItem
					name="Username"
					slugName="username"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<HeaderItem
					name="Email"
					slugName="email"
					isCenter={false}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>
				<HeaderItem
					name="Signup Date"
					slugName="createdAt"
					isCenter={true}
					selectedHeader={selectedHeader}
					setSelectedHeader={setSelectedHeader}
					sortOrder={sortOrder}
					setSortOrder={setSortOrder}
				/>

				<th className="p-2 whitespace-nowrap">
					<div className="font-semibold text-center">Role(s)</div>
				</th>

				<th className="absolute p-2 whitespace-nowrap right-0">
					<div className="font-semibold text-left">
						{`Count: ${usersCount}`}
					</div>
				</th>
			</tr>
		);
	};

	return (
		<section className="ml-18 mt-16">
			<div className="p-3">
				<div>
					<div className="overflow-x-auto ">
						<table className="table-auto w-full">
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<thead className="text-xs font-bold uppercase dark:text-primary-text-dark text-primary-text-light">
									<TableHeaders />
								</thead>
							)}
							{isFetching || isLoading || isError ? (
								<></>
							) : (
								<tbody className="text-sm divide-y divide-gray-300 dark:divide-gray-600 transition-all duration-500">
									{data.users.map((item: any) => (
										<TableData
											key={item._id}
											id={item._id}
											username={item.username}
											email={item.email}
											signupDate={item.createdAt}
											roles={item.roles}
											photo={
												item.photo != null
													? item.photo
													: `https://api.dicebear.com/7.x/bottts/svg?seed=${item.username}`
											}
										/>
									))}
								</tbody>
							)}
						</table>
					</div>
				</div>
				{isFetching || isLoading ? (
					<div>
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
						<TableLoadingSkeleton />
					</div>
				) : isError ? (
					<div className="h-full ml-0 ">
						<div className=" flex flex-col mx-auto my-auto text-center align-middle overflow-clip h-full ">
							<div className="mx-auto my-auto self-center">
								<div className="min-h-16">
									<DataNotFoundAnimation height={dimensions.height / 1.5} />
								</div>
								<h3 className="lg:text-2xl text-xl font-bold dark:text-gray-200 text-gray-700 -mt-8">
									{"Couldn't find the data you're looking for!"}
								</h3>
							</div>
						</div>
					</div>
				) : (
					<Pagination
						itemsPerPage={data.meta.limit}
						totalCount={data.meta.total}
						totalPages={data.meta.pages}
						currentPage={page}
						setPage={setPage}
					/>
				)}
			</div>

			{/* <ReactQueryDevtools initialIsOpen /> */}
		</section>
	);
}

type tableDateType = {
	id: string;
	username: string;
	email: string;
	signupDate: string;
	roles: string[];
	photo: string;
};

const TableData = ({
	id,
	username,
	email,
	signupDate,
	roles,
	photo
}: tableDateType) => {
	const navigate = useNavigate();
	return (
		<tr
			className="cursor-pointer"
			onClick={() => {
				navigate(`../user/${id}`);
			}}
		>
			<td className="w-4/12 md:w-4/12 p-2 break-all">
				<div className="flex items-center">
					<div className="w-10 h-10 flex justify-center flex-shrink-0 mr-2 sm:mr-3">
						<LazyImage photo={photo} />
					</div>
					<div className="font-medium dark:text-primary-text-dark text-primary-text-light">
						{username}
					</div>
				</div>
			</td>
			<td className=" w-4/12 md:w-3/12 p-2 break-all">
				<div className="text-left dark:text-secondary-text-dark text-secondary-text-light font-medium">
					{email}
				</div>
			</td>
			<td className="md:w-2/12 p-2 whitespace-nowrap">
				<div className="text-center font-medium dark:text-secondary-text-dark text-secondary-text-light">
					{moment(signupDate).format('MM/DD/YYYY - HH:mm')}
				</div>
			</td>
			<td className="md:w-3/12 w-2/12 md:p-2 md:whitespace-nowrap">
				<div className="flex flex-row justify-center">
					{roles.map((tag, index) => {
						return (
							<div
								key={index}
								className={
									'flex justify-center items-center m-1 py-1.5 px-1 rounded-full dark:text-primary-text-dark text-primary-text-light font-semibold  transition-all duration-500 ' +
									(tag === 'admin'
										? 'bg-green-700 border-green-600'
										: tag === 'user'
										? 'bg-gray-700 border-blue-600'
										: 'bg-yellow-700 border-yellow-600')
								}
							>
								<div className="text-xs text-primary-text-dark font-semibold mx-1">
									{tag.toString().toUpperCase()}
								</div>
							</div>
						);
					})}
				</div>
			</td>
		</tr>
	);
};
